import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Instagram } from "lucide-react";
import Monogram from "./Monogram";
import { STUDIO, NAV_LINKS } from "../data/siteContent";
import { scrollToId, stopScroll, startScroll } from "../hooks/useLenis";
import { EASE } from "./Reveal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) stopScroll();
    else startScroll();
  }, [open]);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-ink/90 backdrop-blur-md border-b border-gold/10 py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <a
            href="#hero"
            data-testid="navbar-logo"
            onClick={(e) => go(e, "hero")}
            className="flex items-center gap-3 group"
          >
            <Monogram className="w-8 h-8 sm:w-9 sm:h-9 text-gold group-hover:rotate-6 transition-transform duration-500" />
            <span className="leading-none">
              <span className="block font-display text-lg sm:text-xl font-semibold tracking-[0.18em] text-ivory">
                MOHIT STUDIO
              </span>
              <span className="block text-[9px] uppercase tracking-[0.35em] text-gold mt-1">
                Keshod · Gujarat
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-testid={`nav-link-${l.id}`}
                onClick={(e) => go(e, l.id)}
                className="text-[13px] uppercase tracking-[0.18em] text-ivory-dim hover:text-gold transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#book"
              data-testid="navbar-book-btn"
              onClick={(e) => go(e, "book")}
              className="text-[13px] uppercase tracking-[0.18em] bg-gold text-ink px-6 py-2.5 hover:bg-gold-hover transition-colors duration-300"
            >
              Book Your Date
            </a>
          </nav>

          <button
            className="lg:hidden text-ivory p-2 -mr-2"
            data-testid="navbar-hamburger"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] bg-ink/[0.98] backdrop-blur-xl flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-5">
              <span className="flex items-center gap-3">
                <Monogram className="w-8 h-8 text-gold" />
                <span className="font-display text-lg tracking-[0.18em] text-ivory">
                  MOHIT STUDIO
                </span>
              </span>
              <button
                className="text-ivory p-2 -mr-2"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
              >
                <X className="w-7 h-7" />
              </button>
            </div>

            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={`#${l.id}`}
                  data-testid={`mobile-menu-link-${l.id}`}
                  onClick={(e) => go(e, l.id)}
                  className="font-display text-4xl sm:text-5xl text-ivory py-2 hover:text-gold transition-colors"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.6, ease: EASE }}
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>

            <motion.div
              className="px-8 pb-10 flex flex-col gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6, ease: EASE }}
            >
              <div className="flex flex-col sm:flex-row gap-3">
                {STUDIO.contacts.map((c) => (
                  <a
                    key={c.tel}
                    href={c.tel}
                    data-testid={`mobile-menu-call-${c.tel.replace(/\D/g, "").slice(-4)}`}
                    className="flex items-center gap-2 border border-gold/30 px-4 py-3 text-sm text-ivory-dim hover:border-gold hover:text-gold transition-colors"
                  >
                    <Phone className="w-4 h-4 text-gold" />
                    {c.phone}
                  </a>
                ))}
              </div>
              <a
                href={STUDIO.instagram.url}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-menu-instagram"
                className="flex items-center gap-2 text-sm text-ivory-mute hover:text-gold transition-colors"
              >
                <Instagram className="w-4 h-4" /> {STUDIO.instagram.handle}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
